import { useMemo, useState } from "react";
import {
  ArrowLeft,
  Trash2,
  Save,
  AlertCircle,
  Target,
  Users,
  Rocket,
  MoreHorizontal,
  ExternalLink,
  MessageSquare,
} from "lucide-react";
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Paper,
  Tabs,
  Tab,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useICP } from "../ICPProvider";
import {
  EditableFieldDisplay,
  useEditableICP,
} from "../../../../components/EditableFieldDisplay";
import AIDocumentQA from "../../../../components/AIDocumentQA";
import ICPService from "../../../../services/ICP";
import { useSelector } from "react-redux";
import SendFeedbackDialog from "../../../../components/SendFeedbackDialog";

const ICPResearchDetailView = ({ onBack, icp: initialIcp }: any) => {
  const reduxUser = useSelector((state: any) => state.user);
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [icp, setIcp] = useState(initialIcp);
  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const { editedIcp, setEditedIcp, handleFieldChange } =
    useEditableICP(initialIcp);
  const { updateICP, deleteICP } = useICP();
  const newICPServiceInstance = useMemo(() => new ICPService(), []);

  const handleUpdate = async () => {
    const updated = await updateICP(
      projectId,
      editedIcp._id || editedIcp.id,
      editedIcp
    );
    if (updated) {
      setIsEditing(false);
      setIcp(updated);
      setEditedIcp(updated);
    }
  };

  const FieldDisplay = ({ label, value }: any) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        variant="subtitle2"
        sx={{
          color: "#6B7280",
          fontSize: "0.75rem",
          fontWeight: 500,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: "#111827",
          fontSize: "0.875rem",
        }}
      >
        {Array.isArray(value) ? value.join(", ") : value || "—"}
      </Typography>
    </Box>
  );

  const DynamicField = ({ label, path, type }: any) => {
    // Get value using path from editedIcp
    const value = path
      .split(".")
      .reduce((obj: any, key: string) => obj?.[key], editedIcp);

    return isEditing ? (
      <EditableFieldDisplay
        label={label}
        value={value}
        onChange={handleFieldChange}
        type={type}
        path={path}
      />
    ) : (
      <FieldDisplay label={label} value={value} />
    );
  };

  const handleDelete = async () => {
    const success = await deleteICP(projectId, icp._id || icp.id);
    if (success) {
      onBack();
    }
    setShowDeleteDialog(false);
  };

  const handleMenuClick = (event: any) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const TabPanel = ({ children, value, index }: any) => (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );

  return (
    <Box
      sx={{
        backgroundColor: "#F9FAFB",
        borderRadius: "12px",
        px: { xs: 2, sm: 3, md: 4 },
        py: 4,
      }}
    >
      <Box
        sx={{
          mb: 4,
          display: "flex",
          alignItems: "center",
          gap: 2,
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            onClick={() => {
              if (onBack) onBack();
              navigate(`/project/${projectId}`);
            }}
            sx={{
              padding: { xs: 0.75, sm: 1 },
              backgroundColor: "white",
              border: "1px solid",
              borderColor: "rgba(0, 0, 0, 0.1)",
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.9)",
              },
            }}
          >
            <ArrowLeft size={18} />
          </IconButton>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
                color: "#111827",
              }}
            >
              {icp.name}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#6B7280",
                fontSize: "0.75rem",
              }}
            >
              ICP ID: {icp._id || icp.id}
            </Typography>
          </Box>
        </Box>
        <Box>
          {isEditing ? (
            <>
              <Button
                startIcon={<Save size={16} />}
                variant="contained"
                onClick={handleUpdate}
                sx={{
                  mr: 1,
                  backgroundColor: "#E11D48",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  boxShadow: "0 1px 3px rgba(225, 29, 72, 0.2)",
                  "&:hover": {
                    backgroundColor: "#BE123C",
                    boxShadow: "0 2px 6px rgba(225, 29, 72, 0.3)",
                  },
                  textTransform: "none",
                }}
              >
                Save Changes
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setIsEditing(false);
                  setEditedIcp(initialIcp);
                }}
                sx={{
                  borderColor: "#D1D5DB",
                  color: "#374151",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  "&:hover": {
                    borderColor: "#9CA3AF",
                    backgroundColor: "rgba(0, 0, 0, 0.02)",
                  },
                  textTransform: "none",
                }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <AIDocumentQA
                userId={reduxUser.id}
                documentName={icp.name}
                projectId={projectId}
                documentId={icp._id || icp.id}
                documentType="ICP"
                service={newICPServiceInstance}
              />
              <Button
                onClick={handleMenuClick}
                endIcon={<MoreHorizontal size={16} />}
                variant="outlined"
                sx={{
                  borderColor: "#D1D5DB",
                  color: "#374151",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  "&:hover": {
                    borderColor: "#9CA3AF",
                    backgroundColor: "rgba(0, 0, 0, 0.02)",
                  },
                  textTransform: "none",
                }}
              >
                Actions
              </Button>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                    borderRadius: "8px",
                    border: "1px solid",
                    borderColor: "rgba(0, 0, 0, 0.08)",
                  },
                }}
              >
                {/* <MenuItem
                  onClick={() => {
                    setIsEditing(true);
                    handleMenuClose();
                  }}
                  sx={{ 
                    py: 1.5,
                    px: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit ICP
                </MenuItem> */}
                <MenuItem
                  onClick={() => {
                    setShowFeedbackDialog(true);
                    handleMenuClose();
                  }}
                  sx={{
                    color: "#EF4444",
                    py: 1.5,
                    px: 2,
                    "&:hover": {
                      backgroundColor: "rgba(239, 68, 68, 0.04)",
                    },
                  }}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Feedback
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setShowDeleteDialog(true);
                    handleMenuClose();
                  }}
                  sx={{
                    color: "#EF4444",
                    py: 1.5,
                    px: 2,
                    "&:hover": {
                      backgroundColor: "rgba(239, 68, 68, 0.04)",
                    },
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete ICP
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Box>
      </Box>

      <Paper
        sx={{
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          border: "1px solid",
          borderColor: "rgba(0, 0, 0, 0.08)",
          mb: 4,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          sx={{
            borderBottom: "1px solid",
            borderColor: "rgba(0, 0, 0, 0.08)",
            "& .MuiTabs-indicator": {
              backgroundColor: "#E11D48",
              height: 3,
            },
          }}
        >
          <Tab
            label="Targeting"
            icon={<Target size={16} />}
            iconPosition="start"
            sx={{
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              minHeight: "56px",
              "&.Mui-selected": {
                color: "#E11D48",
              },
            }}
          />
          <Tab
            label="Interests"
            icon={<Users size={16} />}
            iconPosition="start"
            sx={{
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              minHeight: "56px",
              "&.Mui-selected": {
                color: "#E11D48",
              },
            }}
          />
          <Tab
            label="Conversion"
            icon={<Rocket size={16} />}
            iconPosition="start"
            sx={{
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              minHeight: "56px",
              "&.Mui-selected": {
                color: "#E11D48",
              },
            }}
          />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: "8px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                  border: "1px solid",
                  borderColor: "rgba(0, 0, 0, 0.05)",
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    fontSize: "1rem",
                    color: "#111827",
                    mb: 2.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <span className="p-1 bg-blue-50 rounded text-blue-600">
                    <Users size={14} />
                  </span>
                  Professional
                </Typography>
                <DynamicField
                  label="Titles"
                  path="targeting.professional.titles"
                  type="array"
                />
                <DynamicField
                  label="Seniority"
                  path="targeting.professional.seniority"
                  type="array"
                />
                <DynamicField
                  label="Functions"
                  path="targeting.professional.functions"
                  type="array"
                />
                <DynamicField
                  label="Tooling"
                  path="targeting.professional.tooling"
                  type="array"
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: "8px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                  border: "1px solid",
                  borderColor: "rgba(0, 0, 0, 0.05)",
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    fontSize: "1rem",
                    color: "#111827",
                    mb: 2.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <span className="p-1 bg-green-50 rounded text-green-600">
                    <Users size={14} />
                  </span>
                  Demographic
                </Typography>
                <DynamicField
                  label="Locations"
                  path="targeting.demographic.locations"
                  type="array"
                />
                <DynamicField
                  label="Experience"
                  path="targeting.demographic.experience"
                  type="text"
                />
                <DynamicField
                  label="Education"
                  path="targeting.demographic.education"
                  type="array"
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: "8px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                  border: "1px solid",
                  borderColor: "rgba(0, 0, 0, 0.05)",
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    fontSize: "1rem",
                    color: "#111827",
                    mb: 2.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <span className="p-1 bg-purple-50 rounded text-purple-600">
                    <Target size={14} />
                  </span>
                  Industry
                </Typography>
                <DynamicField
                  label="Sectors"
                  path="targeting.industry.sectors"
                  type="array"
                />
                <DynamicField
                  label="Company Size"
                  path="targeting.industry.companySize"
                  type="array"
                />
                <DynamicField
                  label="Company Type"
                  path="targeting.industry.companyType"
                  type="array"
                />
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: "8px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                  border: "1px solid",
                  borderColor: "rgba(0, 0, 0, 0.05)",
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    fontSize: "1rem",
                    color: "#111827",
                    mb: 2.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <span className="p-1 bg-blue-50 rounded text-blue-600">
                    <Target size={14} />
                  </span>
                  Professional
                </Typography>
                <DynamicField
                  label="Topics"
                  path="interests.professional.topics"
                  type="array"
                />
                <DynamicField
                  label="Skills"
                  path="interests.professional.skills"
                  type="array"
                />
                <DynamicField
                  label="Certifications"
                  path="interests.professional.certifications"
                  type="array"
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: "8px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                  border: "1px solid",
                  borderColor: "rgba(0, 0, 0, 0.05)",
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    fontSize: "1rem",
                    color: "#111827",
                    mb: 2.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <span className="p-1 bg-amber-50 rounded text-amber-600">
                    <ExternalLink size={14} />
                  </span>
                  Content
                </Typography>
                <DynamicField
                  label="Formats"
                  path="interests.content.formats"
                  type="array"
                />
                <DynamicField
                  label="Topics"
                  path="interests.content.topics"
                  type="array"
                />
                <DynamicField
                  label="Platforms"
                  path="interests.content.platforms"
                  type="array"
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: "8px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                  border: "1px solid",
                  borderColor: "rgba(0, 0, 0, 0.05)",
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    fontSize: "1rem",
                    color: "#111827",
                    mb: 2.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <span className="p-1 bg-green-50 rounded text-green-600">
                    <Users size={14} />
                  </span>
                  Engagement
                </Typography>
                <DynamicField
                  label="Groups"
                  path="interests.engagement.groups"
                  type="array"
                />
                <DynamicField
                  label="Events"
                  path="interests.engagement.events"
                  type="array"
                />
                <DynamicField
                  label="Influencers"
                  path="interests.engagement.influencers"
                  type="array"
                />
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: "8px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                  border: "1px solid",
                  borderColor: "rgba(0, 0, 0, 0.05)",
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    fontSize: "1rem",
                    color: "#111827",
                    mb: 2.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <span className="p-1 bg-blue-50 rounded text-blue-600">
                    <Target size={14} />
                  </span>
                  Intent Signals
                </Typography>
                <DynamicField
                  label="Signals"
                  path="conversion.intent.signals"
                  type="array"
                />
                <DynamicField
                  label="Strength"
                  path="conversion.intent.strength"
                  type="text"
                />
                <DynamicField
                  label="Timeframe"
                  path="conversion.intent.timeframe"
                  type="text"
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: "8px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                  border: "1px solid",
                  borderColor: "rgba(0, 0, 0, 0.05)",
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    fontSize: "1rem",
                    color: "#111827",
                    mb: 2.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <span className="p-1 bg-purple-50 rounded text-purple-600">
                    <Users size={14} />
                  </span>
                  Authority
                </Typography>
                <DynamicField
                  label="Level"
                  path="conversion.authority.level"
                  type="text"
                />
                <DynamicField
                  label="Constraints"
                  path="conversion.authority.constraints"
                  type="array"
                />
                <DynamicField
                  label="Stakeholders"
                  path="conversion.authority.stakeholders"
                  type="array"
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: "8px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                  border: "1px solid",
                  borderColor: "rgba(0, 0, 0, 0.05)",
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    fontSize: "1rem",
                    color: "#111827",
                    mb: 2.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <span className="p-1 bg-amber-50 rounded text-amber-600">
                    <Rocket size={14} />
                  </span>
                  History
                </Typography>
                <DynamicField
                  label="Engagements"
                  path="conversion.history.engagements"
                  type="array"
                />
                <DynamicField
                  label="Patterns"
                  path="conversion.history.patterns"
                  type="array"
                />
                <DynamicField
                  label="Preferences"
                  path="conversion.history.preferences"
                  type="array"
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: "8px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                  border: "1px solid",
                  borderColor: "rgba(0, 0, 0, 0.05)",
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    fontSize: "1rem",
                    color: "#111827",
                    mb: 2.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <span className="p-1 bg-green-50 rounded text-green-600">
                    <Target size={14} />
                  </span>
                  Resources
                </Typography>
                <DynamicField
                  label="Budget Range"
                  path="conversion.resources.budgetRange"
                  type="text"
                />
                <DynamicField
                  label="Approval Process"
                  path="conversion.resources.approvalProcess"
                  type="text"
                />
                <DynamicField
                  label="Constraints"
                  path="conversion.resources.constraints"
                  type="array"
                />
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* SendFeedbackDialog */}
      <SendFeedbackDialog
        open={showFeedbackDialog}
        onClose={() => setShowFeedbackDialog(false)}
        userId={reduxUser.id}
        projectId={projectId}
        documentId={icp._id || icp.id}
        documentType="icp"
        documentName={icp.name}
      />

      {/* Delete ICP Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            width: "100%",
            maxWidth: "450px",
            p: 2,
          },
        }}
      >
        <DialogTitle
          sx={{ fontWeight: 600, fontSize: "1.25rem", color: "#111827", pb: 1 }}
        >
          Delete ICP Profile
        </DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Box
              sx={{
                backgroundColor: "rgba(239, 68, 68, 0.1)",
                borderRadius: "50%",
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AlertCircle size={20} color="#EF4444" />
            </Box>
            <Typography sx={{ color: "#4B5563", fontSize: "0.95rem" }}>
              Are you sure you want to delete this ICP profile? This action
              cannot be undone.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setShowDeleteDialog(false)}
            sx={{
              borderColor: "#D1D5DB",
              color: "#374151",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "0.875rem",
              fontWeight: 500,
              "&:hover": {
                borderColor: "#9CA3AF",
                backgroundColor: "rgba(0, 0, 0, 0.02)",
              },
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            sx={{
              backgroundColor: "#EF4444",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "0.875rem",
              fontWeight: 500,
              boxShadow: "0 1px 3px rgba(239, 68, 68, 0.2)",
              "&:hover": {
                backgroundColor: "#DC2626",
                boxShadow: "0 2px 6px rgba(239, 68, 68, 0.3)",
              },
              textTransform: "none",
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ICPResearchDetailView;
